import type { LogEntry } from "../../../core/types.js";

type MarkdownParser = unknown;
type ParseMarkdown = (parser: MarkdownParser, content: string) => unknown;

type MessageEmbedField = {
    name?: unknown;
    value?: unknown;
    inline?: boolean;
    parsedName?: unknown;
    parsedValue?: unknown;
};

type MessageEmbed = {
    title?: unknown;
    description?: unknown;
    footer?: {
        text?: unknown;
    };
    fields?: MessageEmbedField[];
    parsedTitle?: unknown;
    parsedDescription?: unknown;
    parsedFooter?: unknown;
};

export type MessageComponentMedia = {
    url?: unknown;
    proxy_url?: unknown;
    proxyURL?: unknown;
    width?: unknown;
    height?: unknown;
    content_type?: unknown;
};

type SelectOption = {
    label?: unknown;
    value?: unknown;
    description?: unknown;
    default?: unknown;
    emoji?: unknown;
};

type SelectDefaultValue = {
    id?: unknown;
    type?: unknown;
    value?: unknown;
    label?: unknown;
    name?: unknown;
};

export type MessageComponent = {
    type?: unknown;
    id?: unknown;
    content?: unknown;
    parsedContent?: unknown;
    components?: MessageComponent[];
    accessory?: MessageComponent;
    media?: MessageComponentMedia;
    items?: MessageComponent[];
    file?: MessageComponentMedia & {
        filename?: unknown;
        name?: unknown;
    };
    accent_color?: unknown;
    accentColor?: unknown;
    divider?: unknown;
    spacing?: unknown;
    description?: unknown;
    parsedDescription?: unknown;
    label?: unknown;
    url?: unknown;
    style?: unknown;
    emoji?: {
        name?: unknown;
        id?: unknown;
        animated?: unknown;
    };
    options?: SelectOption[];
    placeholder?: unknown;
    min_values?: unknown;
    max_values?: unknown;
    custom_id?: unknown;
    default_values?: SelectDefaultValue[];
    channel_types?: unknown;
    value?: unknown;
    values?: unknown;
    disabled?: unknown;
};

type MessageLogExtra = {
    content?: unknown;
    embeds?: MessageEmbed[];
    attachments?: unknown;
    components?: MessageComponent[];
};

export type ParsedMessageLog = LogEntry & {
    extra: MessageLogExtra;
    parsedContent?: unknown;
};

function readMessageExtra(log: LogEntry): MessageLogExtra {
    return log.extra && typeof log.extra === "object"
        ? log.extra as MessageLogExtra
        : {};
}

function parseOptionalMarkdown(parser: MarkdownParser, parse: ParseMarkdown, value: unknown): unknown {
    return typeof value === "string"
        ? parse(parser, value)
        : null;
}

function parseMessageComponent(component: MessageComponent, parser: MarkdownParser, parse: ParseMarkdown): void {
    component.parsedContent = parseOptionalMarkdown(parser, parse, component.content);
    component.parsedDescription = parseOptionalMarkdown(parser, parse, component.description);

    if (component.components?.length) {
        for (const child of component.components)
            parseMessageComponent(child, parser, parse);
    }

    if (component.accessory)
        parseMessageComponent(component.accessory, parser, parse);

    if (component.items?.length) {
        for (const item of component.items)
            parseMessageComponent(item, parser, parse);
    }
}

export function parseMessageLogs(
    logs: LogEntry[],
    parser: MarkdownParser,
    parse: ParseMarkdown,
): ParsedMessageLog[] {
    return logs.map((log) => {
        const extra = readMessageExtra(log);
        const parsedLog = log as ParsedMessageLog;

        parsedLog.extra = extra;
        parsedLog.parsedContent = parseOptionalMarkdown(parser, parse, extra.content);

        if (extra.embeds?.length) {
            for (const embed of extra.embeds) {
                embed.parsedTitle = parseOptionalMarkdown(parser, parse, embed.title);
                embed.parsedDescription = parseOptionalMarkdown(parser, parse, embed.description);
                embed.parsedFooter = parseOptionalMarkdown(parser, parse, embed.footer?.text);

                if (embed.fields?.length) {
                    for (const field of embed.fields) {
                        field.parsedName = parseOptionalMarkdown(parser, parse, field.name);
                        field.parsedValue = parseOptionalMarkdown(parser, parse, field.value);
                    }
                }
            }
        }

        if (extra.components?.length) {
            for (const component of extra.components)
                parseMessageComponent(component, parser, parse);
        }

        return parsedLog;
    });
}
